import React from "react";
import { ChecklistItem, ChecklistResponseValue, DEFAULT_CHECKLIST_OPTIONS } from "../shared/types";
import InfoTip from "../shared/InfoTip";
import Panel from "./Panel";
import { checklistBtn, wsProgressFill, wsProgressTrack } from "./workspaceStyles";

// ----------------------------  ChecklistPanel  ----------------------------
// The checklist of the record, or of the object shown: each question with its
// configured buttons; the picked button's label is the answer.

interface ChecklistPanelProps {
  items: ChecklistItem[];
  responses: Record<string, ChecklistResponseValue>;
  onPick: (itemId: string, response: ChecklistResponseValue) => void;
  showError: boolean;
  disabled: boolean;
}

export const itemOptions = (item: ChecklistItem): string[] => {
  const options = (item.options ?? []).map((o) => o.trim()).filter(Boolean);
  return options.length ? options : DEFAULT_CHECKLIST_OPTIONS;
};

const ChecklistPanel: React.FC<ChecklistPanelProps> = ({
  items,
  responses,
  onPick,
  showError,
  disabled,
}) => {
  const marked = items.filter((i) => responses[i.id]).length;
  const pct = items.length ? Math.round((marked / items.length) * 100) : 100;

  return (
    <Panel title="Checklist">
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <div style={wsProgressTrack}>
          <div
            style={{
              ...wsProgressFill,
              width: pct + "%",
              background: pct === 100 ? "#22c55e" : "#f59e0b",
            }}
          />
        </div>
        <span style={progressText}>
          {marked}/{items.length} · {pct}%
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              ...itemRow,
              background: showError && !responses[item.id] ? "#fee2e2" : "transparent",
            }}
          >
            <span style={{ color: "#374151" }}>
              {item.label}
              <InfoTip text={item.help_text} label={item.label} />
            </span>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {itemOptions(item).map((r, k) => (
                <button
                  key={r}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(item.id, r)}
                  style={checklistBtn(responses[item.id] === r, k, disabled)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

// ----------------------------  Styles  ----------------------------

const progressText: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 500,
  color: "#6b7280",
  whiteSpace: "nowrap",
};

const itemRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "1.125rem",
};

export default ChecklistPanel;
